import { get, set, merge, isEmpty, isObject, round } from 'lodash';
import moment from 'moment';
import { yearMonthDayFormat } from '@enums/date-formats';
import overrideTypes from '@enums/override-types';
import transitionPeriodOverrideStates from '@enums/transition-period-override-states';
import { isSameOrAfter } from '@sharedModules/data/utils/dates-utils';
import { uom, normWeight } from '@enums/price-types';
import {
  saveResultsToDatabricks,
  useStickersPromotion,
  useFirstFuturePrice,
  beforePromotionDays,
  useManualProductGoLiveDate,
  enablePriceOptimisation,
} from '@enums/feature-flags';
import agGridUtils from '../utils/ag-grid-utils';
import { getReferencePriceAtOverride, getSmartOverrideFields } from '../store/utils/price-override';

export default {
  methods: {
    isSmartOverride(item) {
      return get(item, 'overridePrice.expiryDate', null) !== null;
    },

    useOverridePrice(item) {
      return !this.useLockedPrice(item) && this.hasOverride(item);
    },

    hasOverride(item) {
      return get(item, 'overridePrice.price', null) !== null && !!item.scenarioPrice;
    },

    useLockedPrice(item) {
      if (!this.isSmartOverride(item)) return false;

      const smartOverrideEnd = moment(get(item, 'overridePrice.expiryDate'), yearMonthDayFormat);
      let promotions = item.currentPromotion ? [item.currentPromotion] : [];
      let upcomingPromotions = item.upcomingPromotion ? [item.upcomingPromotion] : [];

      if (this.isFeatureFlagEnabled(useStickersPromotion)) {
        const childPromotions = get(item, 'childPromotions.currentPromotion', {});
        const childUpcomingPromotions = get(item, 'childPromotions.upcomingPromotion', {});
        promotions = [...promotions, ...Object.values(childPromotions)];
        upcomingPromotions = [...upcomingPromotions, ...Object.values(childUpcomingPromotions)];
      }

      const days = this.toggleLogic[beforePromotionDays];
      const lockOnPromotion = this.lockPriceOnPromotion(smartOverrideEnd, promotions);
      const lockBeforePromotion = this.lockPriceBeforePromotion(
        smartOverrideEnd,
        upcomingPromotions,
        days
      );

      return lockOnPromotion || lockBeforePromotion;
    },

    lockPriceOnPromotion(smartOverrideEnd, promotions) {
      if (isEmpty(promotions)) return false;
      const promotionEnd = moment.max(promotions.map(p => moment(get(p, 'expiryDate'))));
      return isSameOrAfter(promotionEnd, smartOverrideEnd);
    },

    lockPriceBeforePromotion(smartOverrideEnd, upcomingPromotions, beforePromoDays) {
      if (isEmpty(upcomingPromotions)) return false;
      const lockedPeriods = upcomingPromotions.map(p =>
        this.calculateBeforePromotionPeriod(p, beforePromoDays)
      );
      const lockStart = moment.min(lockedPeriods.map(p => p[0]));
      const lockEnd = moment.max(lockedPeriods.map(p => p[1]));
      return isSameOrAfter(smartOverrideEnd, lockStart) && isSameOrAfter(lockEnd, smartOverrideEnd);
    },

    calculateBeforePromotionPeriod(upcomingPromotion, beforePromoDays) {
      const upcomingPromotionStart = moment(upcomingPromotion.effectiveDate);
      const startDate = upcomingPromotionStart.clone().subtract(beforePromoDays, 'day');
      const endDate = upcomingPromotionStart.clone().subtract(1, 'day');
      return [startDate, endDate];
    },

    getOverridePrice(item) {
      const overridePrice = get(item, 'overridePrice.price', null);
      const price = this.useOverridePrice(item)
        ? this.priceRelevantToPriceType(item, overridePrice)
        : this.scenarioPrice(item);
      return price;
    },

    priceRelevantToPriceType(item, price) {
      if (this.selectedPriceType === uom) {
        return item.contentValue ? round(price / item.contentValue, 2) : price;
      }
      if (this.selectedPriceType === normWeight) {
        return item.normWeight ? round(price * item.normWeight, 2) : price;
      }
      return price;
    },

    scenarioPrice(item) {
      const scenarioPriceField = this.getPriceField('scenarioPrice');
      return item[scenarioPriceField];
    },

    async changeOverridePrice(newValue, item) {
      if (newValue === null || newValue === undefined || Number.isNaN(newValue)) {
        console.warn('Invalid price value, aborting override:', newValue);
        return;
      }

      const intentionPrice = this.getIntentionPrice(item);
      const numericPrice = this.formatStringToNumber(newValue);

      // Additional safety check after parsing
      if (Number.isNaN(numericPrice)) {
        console.warn('Failed to parse price value:', newValue);
        return;
      }

      const usePromoPriceOnSmartOverride = this.useLockedPrice(item);
      const updatedOverridePrice = usePromoPriceOnSmartOverride ? intentionPrice : numericPrice;

      // Update the override price object
      const defaultSmartOverrideFields = getSmartOverrideFields({
        goLiveDate: get(this.selectedWorkpackage, 'goLiveDate'),
        toggleLogic: this.toggleLogic,
      });
      const updatedOverridePriceObject = merge(
        {},
        get(item, 'overridePrice', {}),
        {
          price: updatedOverridePrice,
          overrideDate: moment().format(yearMonthDayFormat),
          overrideTimestamp: moment().unix(),
          // referencePriceAtOverride value has been set in grid-view-ag-grid.vue `handleScenarioPriceValueSetter`
          referencePriceAtOverride: get(item, 'overridePrice.referencePriceAtOverride'),
          isSmart: this.isSmartOverride(item),
          overrideType: overrideTypes.manual,
        },
        // only set default expiry/effective dates if not already a smart override
        // Overwrite the expiry date for active transition period overrides to be the default.
        this.isSmartOverride(item) && !this.isActiveTransitionPeriodOverride(item)
          ? {}
          : defaultSmartOverrideFields
      );
      set(item, 'overridePrice', updatedOverridePriceObject);

      await this.updateOverridePrice({
        item: {
          ...item,
          startingPrice: intentionPrice,
          usePromoPriceOnSmartOverride,
        },
      });
    },

    convertPriceToRegular(item, price) {
      if (this.selectedPriceType === uom) {
        return item.contentValue ? round(price * item.contentValue, 2) : price;
      }
      if (this.selectedPriceType === normWeight) {
        return item.normWeight ? round(price / item.normWeight, 2) : price;
      }
      return price;
    },

    /**
     * Captures the current row order from the grid and stores it in stableSortRowOrder,
     * freezing sort positions until the user deliberately re-sorts a column.
     * Called before any state mutation that would otherwise trigger an immediate re-sort
     * (price override save, revert, bulk remove).
     */
    captureStableSortRowOrder() {
      if (!this.gridApi) return;
      const rowOrder = [];
      this.gridApi.forEachNodeAfterFilterAndSort(node => {
        if (node.id !== undefined && node.id !== null) rowOrder.push(node.id);
      });
      this.stableSortRowOrder = rowOrder.length ? rowOrder : null;
    },

    async handleRevertButtonClick(item) {
      const intentionPrice = this.getIntentionPrice(item);
      const referencePriceAtOverride = get(item, ['overridePrice', 'referencePriceAtOverride']);

      // Capture current row order before mutating state so postSortRows can freeze positions,
      // matching the same stable-sort behaviour applied when saving a price override.
      this.captureStableSortRowOrder();

      // Reset the override price
      const updatedOverridePriceObject = {
        price: null,
        overrideDate: null,
        overrideTimestamp: null,
        referencePriceAtOverride: null,
        expiryDate: null,
        expiryTimestamp: null,
        effectiveDate: null,
        effectiveTimestamp: null,
        isSmart: false,
        overrideType: null,
      };

      set(item, 'overridePrice', updatedOverridePriceObject);

      // Determine if this is a saved override (no unsaved KPI results = was saved to Databricks)
      const isSavedOverride = !item.hasUnsavedKpiResults;

      await this.updateOverridePrice({
        item: {
          ...item,
          startingPrice: intentionPrice || referencePriceAtOverride,
          revert: !isSavedOverride, // For saved overrides: revert=false so hasUnsavedKpiResults=true
        },
      });

      // Don't manually override hasUnsavedKpiResults - let KPI calculation determine it
      // Saved overrides: KPI results calculated → hasUnsavedKpiResults = true → Save button orange
      // Unsaved overrides: revert=true → hasUnsavedKpiResults = false → Save button disabled
    },

    getIntentionPrice(item) {
      const productIntentionPrice = get(item, 'intentionPrice.price', null);
      const productLivePrice = get(item, 'livePrice');

      if (this.isFeatureFlagEnabled(useFirstFuturePrice)) {
        const inferredGoLiveDate = moment(get(item, 'inferredGoLiveDate'), null);
        const futurePriceEffectiveDate = moment(get(item, 'intentionPrice.effectiveDate'), null);
        const isValidFuturePrice = isSameOrAfter(inferredGoLiveDate, futurePriceEffectiveDate);

        const numericLivePrice = isObject(productLivePrice)
          ? productLivePrice.price
          : productLivePrice;

        return productIntentionPrice && isValidFuturePrice
          ? productIntentionPrice
          : numericLivePrice;
      }

      if (this.isFeatureFlagEnabled(useManualProductGoLiveDate)) {
        const inferredGoLivePrice = get(item, 'inferredGoLivePrice');
        return inferredGoLivePrice;
      }

      return productIntentionPrice;
    },

    // Helper for asynchronous operations during KPI recalculation
    async recalculateKPIWrapper({ callback, item, rowNode } = {}) {
      if (!this.isFeatureFlagEnabled(saveResultsToDatabricks)) {
        await callback();
        agGridUtils.refreshGridCells(this.gridApi, rowNode);
        return;
      }
      this.$set(item, 'recalculatingKPIs', true);
      try {
        await callback();
        agGridUtils.refreshGridCells(this.gridApi, rowNode);
      } finally {
        this.$set(item, 'recalculatingKPIs', false);
      }
    },
    async removePriceLock(item) {
      // Only update if there's an override price with an expiry date
      if (!get(item, 'overridePrice.expiryDate')) {
        return;
      }

      // Keep the price override but remove the expiry date (smart override)
      const updatedOverridePriceObject = merge({}, get(item, 'overridePrice', {}), {
        expiryDate: null,
        expiryTimestamp: null,
        effectiveDate: null,
        effectiveTimestamp: null,
        isSmart: false,
        price: null,
        referencePriceAtOverride: null,
        overrideDate: null,
        overrideTimestamp: null,
      });

      set(item, 'overridePrice', updatedOverridePriceObject);

      // Get intention price for alert parameters
      const intentionPrice = this.getIntentionPrice(item);

      // Update the override price on the server
      await this.updateOverridePrice({
        item: {
          ...item,
          startingPrice: intentionPrice,
        },
      });
    },
    // Check if the "Remove Price Lock" button should be shown
    showRemoveLockButton(item) {
      return get(item, 'overridePrice.expiryDate', null) !== null;
    },

    // Get the label for the "Remove Price Lock" button
    getRemoveLockButtonText() {
      return this.$t('gridView.removePriceLock');
    },

    // Handle the "Remove Price Lock" action
    handleRemovePriceLock(params, item) {
      // Call the reset method from date-picker-editor to reset the date picker
      const cellEditor = params.api.getCellEditorInstances({
        columns: [params.column],
        rowNodes: [params.node],
      })[0];

      if (cellEditor) {
        cellEditor.reset();
      }

      // Trigger KPI recalculation
      this.recalculateKPIWrapper({
        callback: () => this.removePriceLock(item),
        item,
        rowNode: params.node,
      });

      // Close the editor
      params.api.stopEditing();
    },

    // Get the previous value for the override price
    getSavedReferencePriceAtOverride(item) {
      const referencePriceAtOverride = get(item, ['overridePrice', 'referencePriceAtOverride'], 0);
      return this.priceRelevantToPriceType(item, referencePriceAtOverride);
    },

    /**
     * Determines the reference price to use when creating or updating a price override.
     *
     * This method selects the appropriate reference price based on the optimisation engine used:
     * - If XEO (Cross-Elasticity Engine Optimisation) is enabled and was the last engine to run,
     *   returns the cross-elasticity price.
     * - Otherwise, returns the optimised price from SEO (Strategic Engine Optimisation) if available,
     *   falling back to the scenario price.
     *
     * The reference price is stored with the override to track which price was in effect when
     * the override was created, allowing for price change tracking and analysis.
     *
     * @param {Object} item - The product item containing price information.
     * @param {string} [item.lastEngineSource] - The source of the last engine calculation ('crossElasticity' or other).
     * @param {number} [item.optimizedPrice] - The optimised price from SEO. Does not consider promotion or previous overrides.
     * @param {number} [item.scenarioPrice] - The scenario price, used as fallback.
     *        This is the last engine final price from either SEO or XEO if both are enabled.
     * @param {number} [item.crossElasticityPrice] - The optimised price from XEO when enabled.
     * @returns {number} The referencePriceAtOverride to use.
     */
    getReferencePriceAtOverride(item) {
      const xeoEnabled = this.isFeatureFlagEnabled(enablePriceOptimisation);
      // If SEO was triggered last and the product is missing SEO optimised price,
      // ReferencePriceAtOverride should fall back to the scenario price,
      // which represents the final SEO engine price and should match the strategic price.
      const ignoreSEOScenarioPrice = false;
      return getReferencePriceAtOverride(xeoEnabled, item, ignoreSEOScenarioPrice);
    },

    /**
     * Determines if the given price override is an active transition period override.
     * @param {Object} item - The price override item.
     * @returns True if the override is a transition period override and its state is active, False otherwise.
     */
    isActiveTransitionPeriodOverride(item) {
      return (
        get(item, 'overridePrice.overrideType', null) === overrideTypes.transitionPeriod &&
        get(item, 'transitionPeriodOverrideMetadata.state', null) ===
          transitionPeriodOverrideStates.active
      );
    },
  },
};
